<template>
  <div v-if="props.showModal" class="progress-modal" data-test="indicator-progress-modal">
    <div class="progress-modal-content scale-up-center">
      <h2 class="text-center">
        {{ props.modalTitle }}
      </h2>
      <div class="progress-modal-body">
        <p>{{ props.modalMessage }}</p>
        <div class="progress-steps">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="step"
            :style="{ flex: index === steps.length - 1 ? 0 : 1 }"
          >
            <div
              class="circle"
              :class="{ completed: index < currentStep, current: index === currentStep }"
            >
              <img
                v-if="index < currentStep"
                src="../../assets/button-icons/tick-icon-white.svg"
                alt="completed step"
              />
              <p class="circle-text">{{ step.title }}</p>
            </div>
            <div
              v-if="index !== steps.length - 1"
              class="line"
              :class="{
                thick: index < currentStep && index < steps.length - 1,
                dashed: index === currentStep || index > currentStep,
              }"
            ></div>
          </div>
        </div>
      </div>
      <div class="center-flex next-button">
        <button type="button" class="btn btn-primary" @click="onNextClick">Next</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Step } from "./indicator-steps";

const props = defineProps<{
  showModal: boolean;
  modalTitle: string;
  modalMessage: string;
  currentStep: number;
  steps: Step[];
}>();

const emit = defineEmits(["on-next-click"]);

function onNextClick() {
  emit("on-next-click");
}
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.progress-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999999999;
}

.progress-modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  width: 75%;
  height: clamp(300px, 500px, 75%);

  @media (max-width: 768px) {
    width: 90%;
    height: 90%;

    h2 {
      margin-top: 0;

      @media (max-height: 400px) {
        margin-top: 20px;
        font-size: 20px;
        margin: 0;
      }
    }
  }
}

.progress-modal-body {
  width: 75%;
  min-width: 300px;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4rem;
  margin: 0 auto;
  padding: 0 0 2rem;
  @media (max-width: 768px) {
    height: auto;
    gap: 1rem;
  }
}

.scale-up-center {
  -webkit-animation: scale-up-center 0.3s cubic-bezier(0.39, 0.575, 0.565, 1) both;
  animation: scale-up-center 0.3s cubic-bezier(0.39, 0.575, 0.565, 1) both;
}

.progress-container {
  width: 100%;
}

.next-button {
  margin-top: 2rem;
  width: 100%;
}

@-webkit-keyframes scale-up-center {
  0% {
    -webkit-transform: scale(0.75);
    transform: scale(0.75);
    opacity: 0;
  }
  100% {
    -webkit-transform: scale(1);
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes scale-up-center {
  0% {
    -webkit-transform: scale(0.75);
    transform: scale(0.75);
    opacity: 0;
  }
  100% {
    -webkit-transform: scale(1);
    transform: scale(1);
    opacity: 1;
  }
}

// ----- progress styles here ------
.progress-steps {
  display: flex;
  justify-content: space-between; /* Distribute the steps evenly */
  align-items: center;
  width: 100%;
  max-width: 600px; /* Make sure it takes the width of the parent */
  background-color: white;
  padding: 0 5rem;
  height: auto;
}

.step {
  display: flex;
  align-items: center;
}

.circle {
  position: relative;
  width: 30px;
  height: 30px;
  border: 2px solid #dddddd;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: @form-text-color;
}

.circle-text {
  position: absolute;
  top: 4rem;
  font-size: 18px;
  text-align: center;
}

.circle.completed {
  background-color: @charismatic-color;
  border: 2px solid @charismatic-color;
}

.circle.current {
  border: 8px solid @charismatic-color;

  .circle-text {
    top: calc(4rem - 8px);
  }
}

.line {
  flex-grow: 1;
  height: 2px;
  background-color: #dddddd;
}

.line.thick {
  height: 4px;
  background-color: @charismatic-color;
}

.line.dashed {
  background-color: transparent;
  border: 2px dashed #dddddd;
}
</style>
